import { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import StandaloneWrapper from "standalone/StandaloneWrapper";
import ReviewPage from "./ReviewPage";
import { useRequest } from "standalone/hooks/useRequest";
import normalizer from "services/store/middlewares/api/json-api-normalizer/normalizer";
import { Alert, Spin } from "antd";
import FormattedMessage from "components/common/FormattedMessage";
import Center from "components/common/Center";

const API_URL = import.meta.env.VITE_API_URL;

const ReviewPageWrapper = () => {
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewData, setReviewData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const getReview = useRequest({});

  useEffect(() => {
    const path = window.location.pathname;
    const id = path.substring(path.lastIndexOf("/") + 1);
    if (id) setReviewId(id);
  }, []);

  useEffect(() => {
    if (!reviewId) return;
    setError(null);
    setIsLoading(true);
    getReview
      .call(() => {
        return fetch(`${API_URL}v1/reviews/${reviewId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        })
          .then((res) => {
            if (!res.ok) {
              setError("errorInvalidLink");
              return;
            }
            return res.json();
          })
          .then((data) => {
            const customizationData = data?.included.find(
              (i: any) => i.type === "project_review_customizations"
            );
            const normalizedCustomization = normalizer({
              data: customizationData,
            });
            const google_button_link =
              customizationData?.attributes?.google_button_link ?? null;
            const booksy_button_link =
              customizationData?.attributes?.booksy_button_link ?? null;
            const customizationAttrs =
              customizationData?.attributes?.customization || {};
            const normalizedData = {
              ...normalizer(data),
              customization: {
                ...normalizedCustomization,
                ai_enabled: customizationAttrs.ai_enabled,
                text: customizationAttrs.text,
                colors: customizationAttrs.colors,
              },
              selected_opinion_keywords: normalizer({
                data: data?.included.find(
                  (i: any) => i.type === "project_keywords"
                ),
              })?.selected_opinion_keywords,
              google_button_link,
              booksy_button_link,
            };
            delete normalizedData.customization.customization;

            setReviewData(normalizedData);
            return normalizedData;
          });
      })
      .catch((err) => {
        console.error("Error fetching review:", err);
        setError("Error fetching review");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [reviewId]);

  const content = useMemo(() => {
    if (error || !reviewId) return (
      <Alert
            message={
              <FormattedMessage
                id="standalone.reviews.errors.invalidReviewLink.message"
                defaultMessage="Invalid review link"
                description="Reviews: Tytuł błędu gdy kod w linku jest niepoprawny lub wygasł."
              />
            }
            description={
              <FormattedMessage
                id="standalone.reviews.errors.invalidReviewLink.description"
                defaultMessage="This review link is either invalid or expired."
                description="Reviews: Opis błędu gdy kod w linku jest niepoprawny lub wygasł."
              />
            }
            type="error"
            showIcon
          />
    );
    if (isLoading || !reviewData) return <Spin />;
    return <ReviewPage reviewId={reviewId} reviewData={reviewData} />;
  }, [error, reviewId, isLoading, reviewData]);

  return (
    <StandaloneWrapper 
    extraTranslations={reviewData?.customization?.text}
    translationPrefix="standalone.reviews"
    >
      <Center>
        {content}
      </Center>
    </StandaloneWrapper>
  );
};

const container = document.getElementById("root");

const root = createRoot(container!);
root.render(<ReviewPageWrapper />);
